import { useCallback, useEffect, useRef, useState } from 'react';

import { anglesFromEvent } from '@/app/location/compassHeading';

export type CompassPermissionState = 'granted' | 'prompt' | 'denied' | 'unsupported';

type OrientationApi = { requestPermission?: () => Promise<string> };

const WAIT_FOR_FIRST_BEARING_MS = 4000;

const orientationApi = (): OrientationApi | undefined =>
  window.DeviceOrientationEvent as unknown as OrientationApi | undefined;

const orientationEventName = (): string =>
  ('ondeviceorientationabsolute' in window ? 'deviceorientationabsolute' : 'deviceorientation');

const needsAPrompt = (api: OrientationApi | undefined): boolean => typeof api?.requestPermission === 'function';

const waitForABearing = (): Promise<void> => new Promise((resolve, reject) => {
  const eventName = orientationEventName();
  const onReading = (event: Event) => {
    if (!anglesFromEvent(event as DeviceOrientationEvent)) {
      return;
    }
    window.clearTimeout(timer);
    window.removeEventListener(eventName, onReading);
    resolve();
  };
  const timer = window.setTimeout(() => {
    window.removeEventListener(eventName, onReading);
    reject(new Error('the device reports no absolute bearing'));
  }, WAIT_FOR_FIRST_BEARING_MS);

  window.addEventListener(eventName, onReading);
});

export default function useCompassPermission(enabled: boolean) {
  const [state, setState] = useState<CompassPermissionState>(() =>
    (orientationApi() ? 'prompt' : 'unsupported'),
  );

  const request = useCallback(async() => {
    const api = orientationApi();
    if (!api) {
      setState('unsupported');
      throw new Error('this device has no orientation sensor');
    }
    if (needsAPrompt(api)) {
      const answer = await api.requestPermission!();
      if (answer !== 'granted') {
        setState('denied');
        throw new Error('the compass prompt was declined');
      }
    }
    try {
      await waitForABearing();
    } catch (error) {
      setState('denied');
      throw error;
    }
    setState('granted');
  }, []);

  const hasAskedOnMount = useRef(false);

  useEffect(function askOnMountWhereNoPromptIsNeeded() {
    if (!enabled || hasAskedOnMount.current || needsAPrompt(orientationApi())) {
      return;
    }
    hasAskedOnMount.current = true;
    request().catch(() => undefined);
  }, [enabled, request]);

  return { state, request };
}
